import type { Room } from "@/types/api";
import CloseIcon from "@mui/icons-material/Close";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { inventoryApi } from "./api";
import { furnitureKeys } from "./queryKeys";

interface Props {
  open: boolean;
  furnitureId: number | null;
  furnitureName?: string;
  onClose: () => void;
  onNavigate?: (stagingId: number, roomId: number) => void;
}

function roomLabel(room: Room | null | undefined): string {
  if (!room) return "—";
  const addr = room.stagingInfo?.address ?? "";
  const name = room.name ?? "";
  return addr ? `${addr} (${name})` : name || "—";
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export function ReservationHistoryDialog(
  { open, furnitureId, furnitureName, onClose, onNavigate }: Props,
) {
  const nav = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  function handleGoTo(stagingId: number, roomId: number) {
    if (onNavigate) {
      onNavigate(stagingId, roomId);
      onClose();
    } else {
      void nav(`/staging/${stagingId}/room-builder/${roomId}`);
    }
  }

  const history = useQuery({
    queryKey: furnitureKeys.reservationHistory(furnitureId),
    queryFn: () => inventoryApi.listReservationHistory(furnitureId as number),
    enabled: open && furnitureId !== null,
  });

  const entries = history.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isSmall}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Reservation History
          </Typography>
          {furnitureName && (
            <Typography variant="caption" color="text.secondary">
              {furnitureName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {history.isLoading
          ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )
          : history.isError
          ? <Alert severity="error">Failed to load reservation history.</Alert>
          : entries.length === 0
          ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              No reservation history for this furniture.
            </Typography>
          )
          : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: { xs: 560, sm: 0 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 200 }}>RESERVATION DATE</TableCell>
                    <TableCell sx={{ width: 90 }}>ITEM</TableCell>
                    <TableCell>ROOM</TableCell>
                    <TableCell align="center" sx={{ width: 80 }}>
                      GO TO
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => {
                    const room = entry.roomInfo;
                    const stagingId = room?.stagingInfo?.id ?? room?.staging;
                    const canNavigate = !!room && stagingId != null;
                    return (
                      <TableRow key={entry.id}>
                        <TableCell>{formatDate(entry.reservedAt)}</TableCell>
                        <TableCell>#{entry.furnitureItem}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{roomLabel(room)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          {canNavigate
                            ? (
                              <Tooltip title="Open in Room Builder">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleGoTo(stagingId, room.id)}
                                >
                                  <MeetingRoomIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )
                            : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </DialogContent>
    </Dialog>
  );
}
